import {
  ObservedDay,
  WeatherFactors,
  weatherMultiplier,
} from "./observedWeather";
import { ForecastSeason, seasonOf } from "./season";

/**
 * Builds a per-day seeding plan from trend settings.
 *
 * The point is a seed with SHAPE. A flat figure every day teaches the forecast
 * model nothing — it fits a constant and every driver reads as zero. Weekday
 * shape, weather response, growth, holidays and event rules each give it
 * something real to find, and every one is per-day editable afterwards.
 *
 * Every shaping knob is a plain multiplier: 1.0 leaves a day untouched, 0.25
 * aims for 25% of base, 1.25 for 125%. `tipRatePercent` and `noise` stay
 * percentages — a rate on a sale and a ± spread are not levels. Season
 * multipliers let a 90-day window that spans two seasons teach the forecast
 * a real split; they do not change the model weights themselves.
 */

/** 0 = Sunday, matching `Date.getUTCDay()`. */
export type WeekdayShape = Record<number, number>;

export interface DayEventRule {
  key: string;
  label: string;
  /** Dates already on the merchant event; the weight applies on these only. */
  days: string[];
  /** Revenue multiplier while the rule is active. 1.0 = no effect. */
  multiplier: number;
}

export interface DayPlanSettings {
  baseRevenue: number;
  /** 0 omits that weekday from the plan entirely — a closed day, not a €0 one. */
  weekdayShape: WeekdayShape;
  weatherFactors: WeatherFactors;
  /** Client-side only: compounds takings per 30 days before the seed is posted. */
  growthPerMonth: number;
  /** Applied to days in `holidayDates` (mock holiday overlay). */
  holidayFactor: number;
  /**
   * Takings multiplier per meteorological season. 1.0 leaves that season
   * untouched. Applied from the day's calendar month, same seasons the
   * forecast model uses.
   */
  seasonFactors: Record<ForecastSeason, number>;
  /** Random spread per day, e.g. 0.05 for ±5%, so days are not a clean curve. */
  noise: number;
  salesPerDay: number;
  /** Percent, as the form collects it. */
  tipRatePercent: number;
  eventRules: DayEventRule[];
}

export interface DayPlanRow {
  date: string;
  observed?: ObservedDay;
  netRevenue: number;
  transactionCount: number;
  /** Percent, kept in the unit the table edits. */
  tipRatePercent: number;
  eventTags: string[];
  /** Why the figure is what it is — shown as a column, not sent. */
  factors: {
    weekday: number;
    weather: number;
    growth: number;
    event: number;
    holiday: number;
    season: number;
  };
}

export const DEFAULT_WEEKDAY_SHAPE: WeekdayShape = {
  0: 0.75, // Sun
  1: 0.7, // Mon
  2: 0.8, // Tue
  3: 0.9, // Wed
  4: 1.05, // Thu
  5: 1.35, // Fri
  6: 1.45, // Sat
};

export const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const DEFAULT_SEASON_FACTORS: Record<ForecastSeason, number> = {
  winter: 1,
  spring: 1,
  summer: 1,
  autumn: 1,
};

export function buildDayPlan(params: {
  dates: string[];
  settings: DayPlanSettings;
  observed: Record<string, ObservedDay>;
  /** Mock-holiday overlay dates; `holidayFactor` applies on these only. */
  holidayDates?: string[];
  random?: () => number;
}): DayPlanRow[] {
  const { dates, settings, observed } = params;
  const holidaySet = new Set(params.holidayDates ?? []);
  const rows: DayPlanRow[] = [];

  dates.forEach((date, index) => {
    const weekday = new Date(`${date}T00:00:00.000Z`).getUTCDay();
    const weekdayFactor = settings.weekdayShape[weekday] ?? 1;

    // A closed day is absent, not a €0 row — the server rejects netRevenue <= 0,
    // and a full rebuild fills the gap with its own proper closed row.
    if (weekdayFactor === 0) return;

    const observedDay = observed[date];
    const weatherFactor = weatherMultiplier(
      observedDay,
      settings.weatherFactors
    );
    const growthFactor = Math.pow(settings.growthPerMonth, index / 30);

    const rules = settings.eventRules.filter((rule) =>
      rule.days.includes(date)
    );
    const eventFactor = rules.reduce(
      (product, rule) => product * rule.multiplier,
      1
    );
    const holidayFactor = holidaySet.has(date) ? settings.holidayFactor : 1;
    const season = seasonOf(date);
    const seasonFactor = settings.seasonFactors[season] ?? 1;

    const roll = params.random ? params.random() : unitNoise(date);
    const jitter = 1 + (roll * 2 - 1) * settings.noise;

    const netRevenue =
      settings.baseRevenue *
      weekdayFactor *
      weatherFactor *
      growthFactor *
      eventFactor *
      holidayFactor *
      seasonFactor *
      jitter;

    rows.push({
      date,
      observed: observedDay,
      netRevenue: Math.round(netRevenue * 100) / 100,
      transactionCount: settings.salesPerDay,
      tipRatePercent: settings.tipRatePercent,
      eventTags: rules.map((rule) => rule.label).filter(Boolean),
      factors: {
        weekday: weekdayFactor,
        weather: weatherFactor,
        growth: growthFactor,
        event: eventFactor,
        holiday: holidayFactor,
        season: seasonFactor,
      },
    });
  });

  return rows;
}

/** Stable 0–1 roll from the date, so rebuilds do not re-roll takings. */
export function unitNoise(date: string): number {
  let hash = 2166136261;
  for (let i = 0; i < date.length; i += 1) {
    hash ^= date.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) / 4294967295;
}

/** Inclusive local-date range, `YYYY-MM-DD` each — powers a range picker. */
export function expandDateRange(from: string, to: string): string[] {
  const [start, finish] = from <= to ? [from, to] : [to, from];
  const days: string[] = [];
  let cursor = new Date(`${start}T00:00:00.000Z`).getTime();
  const end = new Date(`${finish}T00:00:00.000Z`).getTime();

  for (; cursor <= end; cursor += 24 * 60 * 60 * 1000) {
    days.push(new Date(cursor).toISOString().slice(0, 10));
  }

  return days;
}
